import { Link, useLocation } from 'react-router-dom';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[];
}

const Breadcrumb = ({ items }: BreadcrumbProps) => {
  const location = useLocation();

  // Auto-generate breadcrumbs if not provided
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [{ label: 'Home', path: '/' }];

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      // Convert segment to readable label
      let label = segment.charAt(0).toUpperCase() + segment.slice(1);

      // Handle specific routes
      if (segment === 'treasures' && index === 0) {
        label = 'Treasures';
      } else if (segment === 'reports' && index === 0) {
        label = 'Reports';
      } else if (segment === 'submit') {
        label = 'Submit Find';
      } else if (segment === 'location-test') {
        label = 'Location Test';
      } else if (pathSegments[index - 1] === 'reports') {
        // This is a report ID, show as "Report Details"
        label = 'Report Details';
      } else if (pathSegments[index - 1] === 'treasures') {
        // This is a treasure ID, show as "Treasure Details"
        label = 'Treasure Details';
      }

      breadcrumbs.push({
        label,
        path: index === pathSegments.length - 1 ? undefined : currentPath
      });
    });

    return breadcrumbs;
  };

  const breadcrumbItems = items || generateBreadcrumbs();

  // Don't show breadcrumbs on home page
  if (location.pathname === '/') {
    return null;
  }

  return (
    <nav className="bg-sky-neutral border-b border-urban/5" aria-label="Breadcrumb">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ol className="flex items-baseline py-3 text-sm">
          {breadcrumbItems.map((item, index) => (
            <li key={index} className="flex items-baseline">
              {index > 0 && (
                <svg
                  className="w-4 h-4 text-urban/40 mx-2 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ marginTop: '0.125rem' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              )}
              {item.path ? (
                <Link
                  to={item.path}
                  className="text-urban/70 hover:text-cardboard transition-colors duration-200 font-medium whitespace-nowrap leading-none"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="text-urban font-semibold whitespace-nowrap leading-none" aria-current="page">
                  {item.label}
                </span>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumb;